import React from 'react'
import { Formik } from 'formik'
import { useQuery, useMutation } from  '../../Hooks/graphql_utils'
import { gql } from '@apollo/client'
import { Spinner, ErrorMessage } from '../../Elements'
import { InventorySchema } from '@mmg/validation'
import InventoryCountForm from './InventoryCountForm'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../Hooks/toast'
import _ from 'lodash'


const QUERY = gql`
    query inventoryCountQuery {
        lookupInventoryLocation {
            id
            name
        }
        lookupInventoryType {
            id
            name
            keycode
        }
        lookupProductCategory {
            id
            name
        }
        inventory {
            id
            product {
                id
                name
                description
                category {
                    id
                    name
                }
            }
            count_date
            location {
                id
                name
            }
            unit_count
            individual_count
            total_units
            inventory_cost
        }
    }   
`

const localNow = () => {
    const raw = new Date()
    const timeZoneAdjusted = new Date(raw.getTime() - (raw.getTimezoneOffset() * 60000))
    return timeZoneAdjusted.toISOString().split('T')[0]
}

const DEFAULT_FILTERS_STATE = {
    location: '',
    add_product_type: '',
    update_date: localNow(),
    product_categories: []
}

const MUTATION = gql`
mutation UpdateInventory($inventory: InventoryInput!) {
    updateInventory(inventory: $inventory) {
        id
        product {
            id
            name
            description
            category {
                id 
                name
            }
        }
        count_date
        location {
            id
            name
        }
        unit_count
        individual_count
        total_units
        inventory_cost
    }
}
`

const sortAndMapItems = (inventoryItems) => {
    // Always want Order to be (by Description): 
    // PebbleTec Rock, Pebblesheen Rock, PebbleFina, Other, Additive, Pigment
    // And then alphabetical asc by Name within those description groupings
    const SORTABLE_PRODUCT_DESCRIPTIONS = ['PEBBLETEC ROCK', 'PEBBLESHEEN ROCK', 'PEBBLEFINA ROCK', 'ADDITIVE', 'PIGMENT', 'PIGMENT (OTHER)']
    const sortedByName = _.orderBy(inventoryItems, [(o) => o.product.name.toLowerCase()], ['asc'])

    const ptRock = sortedByName.filter(i => i.product.description === SORTABLE_PRODUCT_DESCRIPTIONS[0])
    const psRock = sortedByName.filter(i => i.product.description === SORTABLE_PRODUCT_DESCRIPTIONS[1])
    const pfRock = sortedByName.filter(i => i.product.description === SORTABLE_PRODUCT_DESCRIPTIONS[2])
    const additive = sortedByName.filter(i => i.product.description === SORTABLE_PRODUCT_DESCRIPTIONS[3])
    const pigment = sortedByName.filter(i => i.product.description === SORTABLE_PRODUCT_DESCRIPTIONS[4])
    const pigmentOther = sortedByName.filter(i => i.product.description === SORTABLE_PRODUCT_DESCRIPTIONS[5])
    const other = sortedByName.filter(i => !SORTABLE_PRODUCT_DESCRIPTIONS.some(d => d === i.product.description))
    const orderedOther = _.orderBy(other, [(o) => o.product.description.toLowerCase()], ['asc'])

    return [...ptRock, ...psRock, ...pfRock, ...orderedOther, ...additive, ...pigment, ...pigmentOther].map((i, valuesIndex) => ({ ...i, valuesIndex }))
}

export default () => {
    const navigate = useNavigate()
    const { showSuccess, showError } = useToast()
    const [updateInventoryCount] = useMutation(MUTATION)

    const { loading, error, data } = useQuery(QUERY, {
    })

    if (loading) {
        return <Spinner />
    }

    if (error) {
        return <ErrorMessage />
    }

    data.inventory = sortAndMapItems(data.inventory)

    const dataWithDefaultFilterState = { ...DEFAULT_FILTERS_STATE, ...data }


    const onSubmit = async (values, { setValues, setTouched }) => {
        let submitValues = {
            update_date: values.update_date,
            inventory: values.inventory.filter((inventory, idx) => {
                const inv = dataWithDefaultFilterState.inventory[idx]
                // eslint-disable-next-line
                return inv.unit_count != inventory.unit_count || inv.individual_count != inventory.individual_count
            })
        }

        const model = InventorySchema.cast(submitValues, { stripUnknown: true })
        await updateInventoryCount({
            variables: {
                inventory: model
            }
        })
            .then(res => {
                showSuccess('Inventory saved!')
                setTouched({})
                const newState = { ...values, inventory: sortAndMapItems(res.data.updateInventory) }
                setValues(newState)
            })
            .catch(() => showError('Error, please try again.'))
    }

    const onReset = () => navigate('/inventory-count')

    return (
        <Formik
            onReset={onReset}
            enableReinitialize
            onSubmit={onSubmit}
            validationSchema={InventorySchema}
            initialValues={dataWithDefaultFilterState}
        >
            {props =>
                <InventoryCountForm
                    {...props}
                    lookupOptions={data}
                />
            }
        </Formik>
    )
}